// Pure builders for the per-questionnaire PDF export on the admin vendor detail
// page. React-free and pdfmake-runtime-free (only its TYPES are imported; the
// caller lazy-loads the engine), so the document structure is unit-tested
// directly — same discipline as vendorCsv.ts / companyDetailTabs.ts.
import type { Content, TDocumentDefinitions } from 'pdfmake/interfaces';
import type { Question, Submission, SubmissionComment } from '@l2u/ui/lib/types.ts';
import type { QuestionnaireGroup } from './companyDetailTabs.ts';
import type { QuestionnaireProgress } from './companyDetailProgress.ts';
import { summarizeAnswer } from '@l2u/ui/lib/answerSummary.ts';
import { sortThread } from '@l2u/ui/lib/commentThread.ts';
import { docDisplayStatusLabel, effectiveDocStatus } from '@l2u/ui/lib/submissionStatus.ts';
import { hasExpiredLiveFile } from '@l2u/ui/lib/documentVersions.ts';

const MUTED = '#94a3b8';

export type QuestionnairePdfQuestion = {
  question: Question;
  submission: Submission | undefined;
  comments: SubmissionComment[];
  /** True when the comment-thread fetch failed for this question's submission. */
  commentsUnavailable: boolean;
};

export type QuestionnairePdfSection = {
  categoryName: string;
  questions: QuestionnairePdfQuestion[];
};

export type QuestionnairePdfInput = {
  companyName: string;
  vendorNumber: string | null;
  questionnaireName: string;
  progress: QuestionnaireProgress;
  /** ISO timestamp; injected by the caller so this stays free of Date.now(). */
  generatedAt: string;
  sections: QuestionnairePdfSection[];
};

// ISO -> YYYY-MM-DD without locale/timezone dependence (keeps output and tests
// deterministic). Falls back to the raw value if it isn't a parseable date.
function dateOnly(iso: string | null | undefined): string {
  if (typeof iso !== 'string' || iso.length < 10) return iso ?? '';
  return iso.slice(0, 10);
}

/**
 * Flatten a questionnaire group + lookup maps into the PDF section model. Pure,
 * so the wrapper component stays thin and this is unit-tested. Comments are keyed
 * by submission id; a question with no submission carries no comments.
 *
 * NOTE: This renders every question in group.sections, whereas the PDF header's
 * progress counter (from questionnaireProgress) counts only visibleIds—the export
 * shows all questions; the progress tally reflects only visible ones.
 */
export function buildPdfSections(
  group: QuestionnaireGroup,
  submissionByQuestion: ReadonlyMap<string, Submission>,
  commentsBySubmission: ReadonlyMap<string, SubmissionComment[]>,
  unavailableCommentSubmissionIds: ReadonlySet<string>,
): QuestionnairePdfSection[] {
  return group.sections.map((section) => ({
    categoryName: section.category.name,
    questions: section.questions.map((question) => {
      const submission = submissionByQuestion.get(question.id);
      const submissionId = submission?.id;
      return {
        question,
        submission,
        comments: submissionId ? (commentsBySubmission.get(submissionId) ?? []) : [],
        commentsUnavailable: submissionId
          ? unavailableCommentSubmissionIds.has(submissionId)
          : false,
      };
    }),
  }));
}

// The vendor's answer, minus the file list (files are rendered separately so we
// can show their expiry dates). "Not answered" when nothing was recorded.
function answerContent(question: Question, submission: Submission | undefined): Content {
  const parts = summarizeAnswer(question, submission).filter((p) => p.kind !== 'files');
  if (parts.length === 0) {
    return { text: 'Not answered', italics: true, color: MUTED };
  }
  const lines: Content[] = [];
  for (const part of parts) {
    if (part.kind === 'text') {
      lines.push({ text: part.value });
    } else if (part.kind === 'list') {
      lines.push({ ul: part.values });
    } else if (part.kind === 'adoptions') {
      const items = part.items.map((a) => (a.option ? `${a.option} (${a.mode})` : a.mode));
      lines.push({ text: `Documents: ${items.join(', ')}` });
    } else if (part.kind === 'signature') {
      lines.push({ text: `Signed by ${part.signerName} on ${dateOnly(part.signedAt)}` });
    }
  }
  return { stack: lines };
}

// Uploaded files as a bulleted list of "name (expires YYYY-MM-DD)". null when the
// submission has no files.
function filesContent(submission: Submission | undefined): Content | null {
  const files = submission?.submission_files ?? [];
  if (files.length === 0) return null;
  return {
    margin: [0, 2, 0, 0],
    ul: files.map((f) =>
      f.expires_at ? `${f.file_name} (expires ${dateOnly(f.expires_at)})` : f.file_name,
    ),
  };
}

// The vendor<->admin comment thread, chronological. null when there is nothing to
// show; a failed fetch renders a muted "(comments unavailable)" note.
function commentsContent(item: QuestionnairePdfQuestion): Content | null {
  if (item.commentsUnavailable) {
    return { text: '(comments unavailable)', italics: true, color: MUTED, margin: [0, 2, 0, 0] };
  }
  if (item.comments.length === 0) return null;
  return {
    margin: [0, 2, 0, 0],
    stack: sortThread(item.comments).map((c) => {
      // Attachments now live in the child `attachments[]` list; fall back to the
      // legacy inline `attachment_name` for comments created before that move.
      const attachmentNotes: Content[] =
        c.attachments.length > 0
          ? c.attachments.map(
              (a) => ({ text: ` [attachment: ${a.attachment_name}]`, italics: true }) as Content,
            )
          : c.attachment_name
            ? [{ text: ` [attachment: ${c.attachment_name}]`, italics: true } as Content]
            : [];
      return {
        text: [
          {
            text: `${c.author_type === 'admin' ? 'Reviewer' : 'Vendor'} — ${dateOnly(c.created_at)}: `,
            bold: true,
          },
          { text: c.body ?? '' },
          ...attachmentNotes,
        ],
      };
    }),
  };
}

function questionContent(item: QuestionnairePdfQuestion, now: Date): Content {
  const blocks: Content[] = [
    { text: item.question.label, bold: true, margin: [0, 6, 0, 1] },
    answerContent(item.question, item.submission),
  ];
  if (item.submission) {
    blocks.push({
      text: `Status: ${docDisplayStatusLabel(
        effectiveDocStatus(
          item.submission.status,
          hasExpiredLiveFile(item.submission.submission_files, now),
        ),
      )}`,
      color: MUTED,
      fontSize: 9,
      margin: [0, 2, 0, 0],
    });
  }
  const files = filesContent(item.submission);
  if (files) blocks.push(files);
  const comments = commentsContent(item);
  if (comments) blocks.push(comments);
  return { stack: blocks, margin: [0, 0, 0, 4] };
}

export function buildQuestionnairePdfDoc(input: QuestionnairePdfInput): TDocumentDefinitions {
  // Expiry is evaluated against the injected generation time (never Date.now())
  // so the document stays deterministic and unit-testable.
  const now = new Date(input.generatedAt);
  const vendorLine = input.vendorNumber
    ? `${input.companyName} (${input.vendorNumber})`
    : input.companyName;

  const header: Content[] = [
    { text: input.questionnaireName, fontSize: 18, bold: true },
    { text: vendorLine, fontSize: 12, margin: [0, 2, 0, 0] },
    {
      text: `Generated ${dateOnly(input.generatedAt)}  ·  ${input.progress.completed} / ${input.progress.total} complete`,
      fontSize: 9,
      color: MUTED,
      margin: [0, 2, 0, 8],
    },
  ];

  const body: Content[] = input.sections.map((section) => ({
    stack: [
      { text: section.categoryName, fontSize: 13, bold: true, margin: [0, 10, 0, 2] },
      ...section.questions.map((q) => questionContent(q, now)),
    ],
  }));

  return {
    info: { title: `${input.companyName} — ${input.questionnaireName}` },
    pageMargins: [40, 40, 40, 40],
    defaultStyle: { fontSize: 10, lineHeight: 1.2 },
    content: [...header, ...body],
  };
}

// Slugged, dated file name: "acme-pty-ltd-safety-induction-2026-06-29.pdf".
export function pdfFileName(
  companyName: string,
  questionnaireName: string,
  generatedAt: string,
): string {
  const slug = (s: string) =>
    s
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '') || 'export';
  return `${slug(companyName)}-${slug(questionnaireName)}-${dateOnly(generatedAt)}.pdf`;
}
